import { Car, Check, Printer, Sparkles, UserX } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { instructorReservations } from '@/api/endpoints'
import { partlyPaidAmount, paymentStatusLabel } from '@/lib/paymentStatus'
import {
  cn,
  formatMoney,
  formatTime,
  getActiveLocale,
  parseIds,
  timeToSlot,
  truncate,
} from '@/lib/utils'
import type { CalendarResponse, ClientReservation, InstructorReservation } from '@/types/api'
import { PaymentType, ReserveStatus } from '@/types/api'

import type { ClipboardEntry } from './CalendarPage'
import { assignLanes, emptyLane, laneCells, type LaneCell } from './lanes'
import { useCenters } from '@/features/centers/useCenters'

interface Props {
  data: CalendarResponse
  blocks: InstructorReservation[]
  readOnly: boolean
  clipboard: ClipboardEntry | null
  onOpenInstructor: (reservation: InstructorReservation | null, day: string) => void
  onOpenClient: (
    reservation: ClientReservation | null,
    day: string,
    part: number,
    blockId: number | null,
  ) => void
  onPaste: (day: string, part: number, blockId: number | null) => void
  /** Drag & drop - drop a lesson onto a slot (or onto an instructor). */
  onDropLesson: (lesson: ClientReservation, day: string, part: number, blockId: number | null) => void
  /** Single click on an instructor - opens the action menu at the pointer. */
  onInstructorMenu: (block: InstructorReservation, x: number, y: number) => void
  /** Single click on a lesson - opens the action menu at the pointer. */
  onLessonMenu: (lesson: ClientReservation, x: number, y: number) => void
  /** Double click on a lesson - opens the editor. */
  onLessonDetail: (lesson: ClientReservation) => void
  onFillFavoritesForDay: (day: string) => void
}

/**
 * Two-letter weekday, in the interface language. The legacy grid used a fixed
 * Slovak list; `Intl` gives "lun."/"Mo"/"Mon", so it is trimmed to the same
 * compact shape the narrow day column was built for.
 */
function weekdayLabel(date: Date, locale: string): string {
  return date
    .toLocaleDateString(locale, { weekday: 'short' })
    .replace(/\./g, '')
    .slice(0, 2)
    .toUpperCase()
}

const DAY_COL = '4.5rem'
const INSTRUCTOR_COL = '11rem'

/** The original 7x21 px sprites, carried over from `www/images` unchanged so
 *  the grid reads exactly like the legacy one. */
const COURSE_ICONS: Record<string, { src: string; alt: string }> = {
  SKI: { src: '/images/ICO_lyze.png', alt: 'SKI' },
  SNB: { src: '/images/ICO_snowboard.png', alt: 'SNB' },
}

/**
 * The instructor's availability strip is tinted by what they teach, so the
 * shape of the day is readable at a glance without touching anything. Values
 * are the legacy `td.s{status}.is` rules, kept to the byte.
 */
const AVAILABILITY = {
  preliminary: '#e8e8e8',
  cancelled: 'rgba(235,31,31,0.2)',
  ski: '#9cb3fe',
  snb: 'rgba(252,94,39,0.8)',
  both: 'rgba(196,94,221,0.8)',
} as const

function availabilityColor(statusId: number | null, courseKeys: string[]): string | undefined {
  if (statusId === ReserveStatus.Cancelled) return AVAILABILITY.cancelled
  if (statusId === ReserveStatus.Preliminary) return AVAILABILITY.preliminary
  const ski = courseKeys.includes('SKI')
  const snb = courseKeys.includes('SNB')
  if (ski && snb) return AVAILABILITY.both
  if (snb) return AVAILABILITY.snb
  if (ski) return AVAILABILITY.ski
  return undefined
}

/** Legacy `.cr .paid-*` - the client's name carries how the lesson was paid. */
const PAYMENT_COLOR: Partial<Record<number, string>> = {
  [PaymentType.Cash]: '#12A00A',
  [PaymentType.Card]: '#4F4FC9',
  [PaymentType.Transfer]: '#0E7490',
  [PaymentType.Credit]: '#7C3AED',
  [PaymentType.Invoice]: '#BC3535',
  [PaymentType.Other]: '#CE9409',
}

/** Čiastočne uhradená lekcia (`partlyPaidAmount`) - ani zelená, ani čierna. */
const PARTLY_PAID_COLOR = '#DB2777'

/** Ski / snowboard pictograms for a set of course keys. */
function CourseIcons({ keys, className }: { keys: string[]; className?: string }) {
  const icons = keys.map((key) => COURSE_ICONS[key.toUpperCase()]).filter(Boolean)
  if (!icons.length) return null
  return (
    <span className={cn('inline-flex items-center gap-[3px]', className)}>
      {icons.map((icon) => (
        <img
          key={icon.alt}
          src={icon.src}
          alt={icon.alt}
          title={icon.alt}
          className="h-[20px] w-auto shrink-0 select-none"
          draggable={false}
        />
      ))}
    </span>
  )
}

/**
 * One continuous table: a day column on the left, then the instructor, then the
 * time slots. Modelled on the legacy screen - dotted grid, tight rows, weekday
 * over the date.
 *
 * A block can hold lessons that overlap in time, so each block renders as one
 * or more *lanes* (see ./lanes.ts) plus a spare empty one, which is how a
 * crossing-time lesson gets booked.
 *
 * Every day also renders a "bez inštruktora" row. It is where lessons with no
 * instructor live, and it is the drag source for assigning them - drop one on
 * an instructor's row and it moves across.
 */
export default function CalendarGrid({
  data,
  blocks,
  readOnly,
  clipboard,
  onOpenInstructor,
  onOpenClient,
  onPaste,
  onDropLesson,
  onInstructorMenu,
  onLessonMenu,
  onLessonDetail,
  onFillFavoritesForDay,
}: Props) {
  const { t, i18n } = useTranslation()
  const { grid } = data
  // `i18n.language` is in the deps so the day column relabels the moment the
  // language changes; the value itself comes from the shared locale map.
  const locale = useMemo(() => getActiveLocale(), [i18n.language])

  // What is currently under the pointer during a drag. Kept here rather than in
  // dataTransfer because dragover handlers cannot read dataTransfer payloads.
  const [dragged, setDragged] = useState<ClientReservation | null>(null)

  const statusById = useMemo(() => new Map(data.statuses.map((s) => [s.id, s])), [data.statuses])
  const courseById = useMemo(() => new Map(data.courses.map((c) => [c.id, c])), [data.courses])
  const langById = useMemo(() => new Map(data.languages.map((l) => [l.id, l])), [data.languages])

  const lessonsByBlock = useMemo(() => {
    const map = new Map<number | 'unassigned', ClientReservation[]>()
    for (const lesson of data.client_reservations) {
      const key = lesson.instructor_reservation_id ?? 'unassigned'
      const list = map.get(key) ?? []
      list.push(lesson)
      map.set(key, list)
    }
    return map
  }, [data.client_reservations])

  const blocksByDay = useMemo(() => {
    const map = new Map<string, InstructorReservation[]>()
    for (const block of blocks) {
      if (!block.start) continue
      const day = block.start.slice(0, 10)
      const list = map.get(day) ?? []
      list.push(block)
      map.set(day, list)
    }
    return map
  }, [blocks])

  const columnTemplate = `${DAY_COL} ${INSTRUCTOR_COL} repeat(${grid.parts}, minmax(2.25rem, 1fr))`

  const dnd: DragContext = {
    dragged,
    onDragStart: setDragged,
    onDragEnd: () => setDragged(null),
    onDrop: (day, part, blockId) => {
      if (dragged) onDropLesson(dragged, day, part, blockId)
      setDragged(null)
    },
  }

  return (
    <div className="card overflow-x-auto">
      <div className="min-w-[54rem]">
        {/* header ruler */}
        <div
          className="grid border-b border-slate-300 bg-white text-[12px]"
          style={{ gridTemplateColumns: columnTemplate }}
        >
          <div className="border-r border-dotted border-slate-300 px-2 py-1 font-semibold">
            {t('calendar.dayColumn')}
          </div>
          <div className="border-r border-dotted border-slate-300 px-2 py-1 font-semibold">
            {t('reservation.instructor')}
          </div>
          {grid.slots.map((slot) => (
            <div
              key={slot.index}
              className={cn(
                'border-r border-dotted border-slate-300 px-1 py-1 text-[11px]',
                Number.isInteger(slot.hour) ? 'font-bold text-slate-800' : 'text-slate-400',
              )}
            >
              {Number.isInteger(slot.hour) ? slot.label : `:${slot.label.slice(-2)}`}
            </div>
          ))}
        </div>

        {grid.dates.map((day, dayIndex) => {
          const dayBlocks = blocksByDay.get(day) ?? []
          const date = new Date(`${day}T00:00:00`)
          const weekday = weekdayLabel(date, locale)
          const isWeekend = date.getDay() === 0 || date.getDay() === 6
          // Lessons with no instructor land in the dedicated row at the bottom
          // of the day rather than in a separate strip.
          const unassigned = (lessonsByBlock.get('unassigned') ?? []).filter(
            (l) => l.start?.slice(0, 10) === day,
          )
          const rowCount =
            dayBlocks.reduce(
              (sum, b) => sum + laneCount(lessonsByBlock.get(b.id) ?? [], grid, readOnly),
              0,
            ) + laneCount(unassigned, grid, readOnly)

          return (
            /* days are told apart by the legacy 6px double rule */
            <div
              key={day}
              className={cn(
                dayIndex > 0 && 'border-t-[6px] border-double border-[#C8BDBE]',
              )}
            >
              {/* rows size to their content - see .cal-lane / .cal-spare */}
              <div className="grid" style={{ gridTemplateColumns: columnTemplate }}>
                {/* day cell spans the whole day */}
                <div
                  className={cn(
                    'flex flex-col items-center justify-center border-r border-dotted border-slate-300 px-2 text-center',
                    isWeekend ? 'bg-brand-50/50' : 'bg-white',
                  )}
                  style={{ gridRow: `span ${rowCount} / span ${rowCount}` }}
                >
                  <span className="text-[13px] leading-tight text-brand-600">
                    {date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })}
                  </span>
                  <span className="text-[13px] font-bold leading-tight text-brand-600">
                    {weekday}
                  </span>
                  {!readOnly ? (
                    <span className="mt-0.5 flex justify-center gap-1">
                      <a
                        href={instructorReservations.dayPdfUrl(day)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-slate-400 hover:text-action-600"
                        title={t('calendar.dayPdf')}
                      >
                        <Printer size={13} />
                      </a>
                      <button
                        type="button"
                        className="text-slate-400 hover:text-action-600"
                        title={t('calendar.fillFavorites')}
                        onClick={() => onFillFavoritesForDay(day)}
                      >
                        <Sparkles size={13} />
                      </button>
                    </span>
                  ) : null}
                </div>

                {dayBlocks.map((block) => (
                  <BlockRow
                    key={block.id}
                    block={block}
                    day={day}
                    grid={grid}
                    lessons={lessonsByBlock.get(block.id) ?? []}
                    readOnly={readOnly}
                    clipboard={clipboard}
                    dnd={dnd}
                    status={statusById.get(block.status_id ?? 0)}
                    courseById={courseById}
                    langById={langById}
                    statusById={statusById}
                    onOpenInstructor={onOpenInstructor}
                    onOpenClient={onOpenClient}
                    onPaste={onPaste}
                    onInstructorMenu={onInstructorMenu}
                    onLessonMenu={onLessonMenu}
                    onLessonDetail={onLessonDetail}
                  />
                ))}

                {/* the always-present "no instructor" row: holds every lesson
                    that has not been assigned yet and doubles as the
                    add-an-instructor affordance */}
                <BlockRow
                  block={null}
                  day={day}
                  grid={grid}
                  lessons={unassigned}
                  readOnly={readOnly}
                  clipboard={clipboard}
                  dnd={dnd}
                  courseById={courseById}
                  langById={langById}
                  statusById={statusById}
                  onOpenInstructor={onOpenInstructor}
                  onOpenClient={onOpenClient}
                  onPaste={onPaste}
                  onInstructorMenu={onInstructorMenu}
                  onLessonMenu={onLessonMenu}
                  onLessonDetail={onLessonDetail}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** How many grid rows one instructor block will occupy. */
function laneCount(
  lessons: ClientReservation[],
  grid: CalendarResponse['grid'],
  readOnly: boolean,
) {
  const lanes = assignLanes(lessons, grid.start_hour, grid.interval, grid.parts)
  return Math.max(1, lanes.length + (readOnly ? 0 : 1))
}

interface DragContext {
  dragged: ClientReservation | null
  onDragStart: (lesson: ClientReservation) => void
  onDragEnd: () => void
  onDrop: (day: string, part: number, blockId: number | null) => void
}

interface BlockRowProps {
  /** `null` renders the "bez inštruktora" row. */
  block: InstructorReservation | null
  day: string
  grid: CalendarResponse['grid']
  lessons: ClientReservation[]
  readOnly: boolean
  clipboard: ClipboardEntry | null
  dnd: DragContext
  status?: { key: string | null; name: string | null; color?: string | null }
  courseById: Map<number, { key: string | null; color?: string | null }>
  langById: Map<number, { key: string | null; color?: string | null }>
  statusById: Map<number, { name: string | null; color?: string | null }>
  onOpenInstructor: (reservation: InstructorReservation | null, day: string) => void
  onOpenClient: (
    reservation: ClientReservation | null,
    day: string,
    part: number,
    blockId: number | null,
  ) => void
  onPaste: (day: string, part: number, blockId: number | null) => void
  onInstructorMenu: (block: InstructorReservation, x: number, y: number) => void
  onLessonMenu: (lesson: ClientReservation, x: number, y: number) => void
  onLessonDetail: (lesson: ClientReservation) => void
}

function BlockRow({
  block,
  day,
  grid,
  lessons,
  readOnly,
  clipboard,
  dnd,
  status,
  courseById,
  langById,
  statusById,
  onOpenInstructor,
  onOpenClient,
  onPaste,
  onInstructorMenu,
  onLessonMenu,
  onLessonDetail,
}: BlockRowProps) {
  const { t } = useTranslation()
  const { multi: multiCenter, nameOf, colorOf } = useCenters()
  const [dropHere, setDropHere] = useState(false)

  // Same click/double-click disambiguation as a lesson: without the delay the
  // first click of a double click would leave the menu open behind the modal.
  const clickTimer = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (clickTimer.current) window.clearTimeout(clickTimer.current)
    },
    [],
  )

  const lanes = useMemo(
    () => assignLanes(lessons, grid.start_hour, grid.interval, grid.parts),
    [lessons, grid.start_hour, grid.interval, grid.parts],
  )

  // One spare lane keeps "book an overlapping lesson" one click away. At least
  // one lane must exist, otherwise the instructor cell gets `grid-row: span 0`.
  const rendered: LaneCell[][] = useMemo(() => {
    const rows = [
      ...lanes.map((lane) => laneCells(lane, grid.parts)),
      ...(readOnly ? [] : [emptyLane(grid.parts)]),
    ]
    return rows.length ? rows : [emptyLane(grid.parts)]
  }, [lanes, grid.parts, readOnly])

  const blockStartSlot = block ? (timeToSlot(block.start, grid.start_hour, grid.interval) ?? 1) : 1
  const blockEndSlot = block
    ? (timeToSlot(block.end, grid.start_hour, grid.interval) ?? grid.parts + 1) - 1
    : grid.parts

  const languages = block
    ? parseIds(block.idslang)
        .map((id) => langById.get(id)?.key)
        .filter(Boolean)
        .join('|')
    : ''
  const courseKeys = block
    ? parseIds(block.idscourses)
        .map((id) => courseById.get(id)?.key)
        .filter((key): key is string => Boolean(key))
    : []

  const [first, ...rest] = (block?.instructor?.full_name ?? '—').split(' ')

  // What the instructor's free time looks like: SKI blue, SNB orange, both
  // purple, dimmed while preliminary, red once cancelled.
  const fill = block ? availabilityColor(block.status_id, courseKeys) : undefined

  /** Dropping straight on the instructor keeps the lesson's time. */
  const canDropOnInstructor =
    !readOnly && dnd.dragged !== null && dnd.dragged.instructor_reservation_id !== (block?.id ?? null)

  function dropOnInstructor() {
    setDropHere(false)
    if (!dnd.dragged) return
    const part = timeToSlot(dnd.dragged.start, grid.start_hour, grid.interval) ?? blockStartSlot
    dnd.onDrop(day, Math.min(Math.max(part, 1), grid.parts), block?.id ?? null)
  }

  return (
    <>
      {/* instructor cell spans every lane of this block */}
      <div
        className={cn(
          'group relative flex flex-col justify-center border-r border-dotted border-slate-300 px-2 py-0.5',
          'row-sep',
          canDropOnInstructor && dropHere && 'drop-target',
        )}
        style={{
          gridRow: `span ${rendered.length} / span ${rendered.length}`,
          // Farba strediska bloku (`sql/040`) - len pri viacerých strediskách.
          ...(multiCenter && block && colorOf(block.center_id)
            ? { boxShadow: `inset 3px 0 0 ${colorOf(block.center_id)}` }
            : {}),
        }}
        onDragOver={(e) => {
          if (!canDropOnInstructor) return
          e.preventDefault()
          setDropHere(true)
        }}
        onDragLeave={() => setDropHere(false)}
        onDrop={(e) => {
          if (!canDropOnInstructor) return
          e.preventDefault()
          dropOnInstructor()
        }}
      >
        {block ? (
          <button
            type="button"
            /* click -> action menu, double click -> the form. Same interaction
               as a lesson, so the grid behaves consistently. */
            onClick={(e) => {
              const { clientX, clientY } = e
              if (clickTimer.current) window.clearTimeout(clickTimer.current)
              clickTimer.current = window.setTimeout(() => {
                clickTimer.current = null
                onInstructorMenu(block, clientX, clientY)
              }, 220)
            }}
            onDoubleClick={() => {
              if (clickTimer.current) {
                window.clearTimeout(clickTimer.current)
                clickTimer.current = null
              }
              if (!readOnly) onOpenInstructor(block, day)
            }}
            className="min-w-0 text-left"
            /* Stredisko len v bubline (`sql/040`) - v riadku by zaberalo miesto;
               vidno ho aj podľa farebného pruhu. */
            title={`${multiCenter ? `${nameOf(block.center_id)}\n` : ''}${formatTime(block.start)}–${formatTime(
              block.end,
            )}${block.note ? `\n${block.note}` : ''}`}
          >
            {/* right padding leaves room for the pictograms, which are pinned
                to the same right edge as the status dot below them */}
            <span className="block truncate pr-6 text-[13px] font-bold leading-tight">
              {first}
            </span>
            {rest.length ? (
              <span className="block truncate pr-6 text-[13px] italic leading-tight text-slate-600">
                {rest.join(' ')}
              </span>
            ) : null}
            {languages ? (
              /* right padding keeps the languages clear of the status dot,
                 which is pinned to the bottom-right corner of the cell */
              <span className="block truncate pr-6 text-[11px] leading-tight text-slate-400">
                {languages}
              </span>
            ) : null}
          </button>
        ) : (
          <button
            type="button"
            disabled={readOnly}
            onClick={() => onOpenInstructor(null, day)}
            className={cn(
              'min-w-0 text-left text-[13px] italic text-slate-400',
              !readOnly && 'hover:not-italic hover:text-action-700',
            )}
            title={t('calendar.assignByDrag')}
          >
            {t('calendar.noInstructor')}
          </button>
        )}

        {/* Both markers share the cell's right edge: the courses on top, the
            block's status directly underneath on the languages line. */}
        {block ? <CourseIcons keys={courseKeys} className="absolute right-1 top-1" /> : null}

        {block ? (
          <span className="absolute bottom-1 right-1 flex items-center gap-1">
            {block.owncar ? <Car size={12} className="text-slate-400" /> : null}
            <span
              className="h-2 w-2 rounded-full ring-1 ring-black/10"
              style={{ backgroundColor: status?.color ?? '#ccc' }}
              title={status?.name ?? ''}
            />
          </span>
        ) : null}

      </div>

      {rendered.map((cells, laneIndex) => {
        const isLastLane = laneIndex === rendered.length - 1
        // The trailing lane exists only so a new (possibly overlapping) lesson
        // can be started on any slot - it stays a thin, clickable sliver.
        const isSpare = !readOnly && isLastLane && lanes.length > 0
        return cells.map((cell) => {
          if (cell.kind === 'lesson') {
            return (
              <LessonCell
                key={`l-${laneIndex}-${cell.slot}-${cell.lesson.id}`}
                lesson={cell.lesson}
                span={cell.span}
                // Okolie boxu v farbe alokácie inštruktora, nie biela mriežka -
                // lekcia tak vizuálne patrí do bloku, v ktorom je.
                fill={fill}
                readOnly={readOnly}
                separator={isLastLane}
                dragging={dnd.dragged?.id === cell.lesson.id}
                onDragStart={dnd.onDragStart}
                onDragEnd={dnd.onDragEnd}
                courseKey={courseById.get(cell.lesson.course_id)?.key ?? ''}
                langKey={langById.get(cell.lesson.lang_id)?.key ?? ''}
                statusColor={statusById.get(cell.lesson.status_id ?? 0)?.color ?? null}
                statusName={statusById.get(cell.lesson.status_id ?? 0)?.name ?? ''}
                onOpenMenu={onLessonMenu}
                onOpenDetail={onLessonDetail}
              />
            )
          }

          const withinBlock = cell.slot >= blockStartSlot && cell.slot <= blockEndSlot

          return (
            <EmptyCell
              key={`e-${laneIndex}-${cell.slot}`}
              day={day}
              slot={cell.slot}
              blockId={block?.id ?? null}
              label={grid.slots[cell.slot - 1]?.label ?? ''}
              withinBlock={withinBlock}
              spare={isSpare}
              fill={withinBlock ? fill : undefined}
              readOnly={readOnly}
              separator={isLastLane}
              hasClipboard={Boolean(clipboard)}
              dnd={dnd}
              onClick={() => {
                if (clipboard) onPaste(day, cell.slot, block?.id ?? null)
                else onOpenClient(null, day, cell.slot, block?.id ?? null)
              }}
            />
          )
        })
      })}
    </>
  )
}

/** A free slot: click to book (or paste), drop a dragged lesson onto it. */
function EmptyCell({
  day,
  slot,
  blockId,
  label,
  withinBlock,
  spare,
  fill,
  readOnly,
  separator,
  hasClipboard,
  dnd,
  onClick,
}: {
  day: string
  slot: number
  blockId: number | null
  label: string
  withinBlock: boolean
  /** Part of the thin trailing lane rather than a real one. */
  spare: boolean
  /** Availability tint, derived from the instructor's courses and status. */
  fill?: string
  readOnly: boolean
  separator: boolean
  hasClipboard: boolean
  dnd: DragContext
  onClick: () => void
}) {
  const [over, setOver] = useState(false)
  const canDrop = !readOnly && dnd.dragged !== null

  return (
    <button
      style={fill && !over ? { backgroundColor: fill } : undefined}
      type="button"
      disabled={readOnly}
      onClick={onClick}
      onDragOver={(e) => {
        if (!canDrop) return
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!canDrop) return
        e.preventDefault()
        setOver(false)
        dnd.onDrop(day, slot, blockId)
      }}
      className={cn(
        'border-r border-dotted border-slate-200 transition-colors',
        spare ? 'cal-spare' : 'cal-lane',
        withinBlock ? 'bg-white' : 'bg-slate-100/60',
        withinBlock && !readOnly && 'hover:brightness-95',
        hasClipboard && withinBlock && 'ring-1 ring-inset ring-amber-400',
        over && 'drop-target',
        separator && 'row-sep',
        readOnly && 'cursor-default hover:bg-inherit',
      )}
      aria-label={`${day} ${label}`}
    />
  )
}

function LessonCell({
  lesson,
  span,
  fill,
  readOnly,
  separator,
  dragging,
  onDragStart,
  onDragEnd,
  courseKey,
  langKey,
  statusColor,
  statusName,
  onOpenMenu,
  onOpenDetail,
}: {
  lesson: ClientReservation
  span: number
  fill?: string
  readOnly: boolean
  separator: boolean
  dragging: boolean
  onDragStart: (lesson: ClientReservation) => void
  onDragEnd: () => void
  courseKey: string
  langKey: string
  statusColor: string | null
  statusName: string
  onOpenMenu: (lesson: ClientReservation, x: number, y: number) => void
  onOpenDetail: (lesson: ClientReservation) => void
}) {
  const { t } = useTranslation()
  const cancelled = lesson.status_id === ReserveStatus.Cancelled
  const preliminary = lesson.status_id === ReserveStatus.Preliminary
  // The legacy grid shows the surname only - it is what fits, and the first
  // name is one hover away in the tooltip.
  const label = lesson.client?.sname || lesson.client?.fname || lesson.client?.full_name || '—'
  const paidColor = partlyPaidAmount(lesson) ? PARTLY_PAID_COLOR : PAYMENT_COLOR[lesson.client_paid]
  // Lekcia na vydanom doklade sa nedá presunúť vôbec (`receiptLocked` v
  // `ClientReservationModal.tsx`); uhradenú áno - presun cenu nemení.
  const locked = lesson.has_issued_receipt
  const paid = lesson.client_paid !== PaymentType.Unpaid

  // A single click opens the menu, a double click the editor. The timer resolves
  // the ambiguity - without it the first click of a double click would flash
  // the menu open before the modal replaced it.
  const clickTimer = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (clickTimer.current) window.clearTimeout(clickTimer.current)
    },
    [],
  )

  function cancelClickTimer() {
    if (clickTimer.current) {
      window.clearTimeout(clickTimer.current)
      clickTimer.current = null
    }
  }

  function handleClick(e: React.MouseEvent) {
    const { clientX, clientY } = e
    cancelClickTimer()
    clickTimer.current = window.setTimeout(() => {
      clickTimer.current = null
      onOpenMenu(lesson, clientX, clientY)
    }, 220)
  }

  function handleDoubleClick() {
    cancelClickTimer()
    onOpenDetail(lesson)
  }

  const tooltip = [
    lesson.client?.full_name || '—',
    `${formatTime(lesson.start)}–${formatTime(lesson.end)} · ${lesson.duration_minutes} ${t('global.minutes')}`,
    [courseKey, langKey, lesson.numbers > 1 ? `${lesson.numbers} ${t('global.persons')}` : null]
      .filter(Boolean)
      .join(' · '),
    `${t('reservation.status')}: ${statusName}`,
    `${t('reservation.paid')}: ${paymentStatusLabel(t, lesson)}`,
    lesson.attendance === 1 ? t('calendar.attendanceTaught') : null,
    lesson.attendance === 2 ? t('calendar.attendanceNoShow') : null,
    lesson.fees_total ? `${t('reservation.price')}: ${formatMoney(lesson.fees_total)}` : null,
    locked ? t('calendar.lockedByReceipt') : paid ? t('calendar.lockedByPayment') : null,
    lesson.client?.tel ? `${t('reservation.phone')}: ${lesson.client.tel}` : null,
    // Poznámka je `TEXT` a môže mať aj dva odstavce; natívny tooltip by
    // z nej urobil stenu textu cez pol obrazovky. Celá je v detaile lekcie.
    lesson.note ? `${t('global.note')}: ${truncate(lesson.note)}` : null,
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <div
      className={cn('cal-lane relative flex p-[1px]', separator && 'row-sep')}
      style={{ gridColumn: `span ${span} / span ${span}`, backgroundColor: fill }}
    >
      <button
        type="button"
        draggable={!readOnly && !locked}
        onDragStart={(e) => {
          // A drag must never leave a pending "open the menu" timer behind.
          cancelClickTimer()
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('text/plain', String(lesson.id))
          onDragStart(lesson)
        }}
        onDragEnd={onDragEnd}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        title={tooltip}
        className={cn(
          'flex min-w-0 flex-1 flex-col items-center justify-center overflow-hidden',
          'rounded-[5px] border border-[#adadad] bg-[#ededed] px-1 leading-tight',
          'transition-shadow hover:shadow-md',
          !readOnly && (locked ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'),
          // preliminary lessons are dimmed, cancelled ones struck through
          preliminary && 'opacity-50',
          dragging && 'opacity-40',
        )}
      >
        <span className="flex w-full items-center justify-center gap-1">
          <CourseIcons keys={[courseKey]} />
          <span
            className={cn(
              'truncate text-[13px] font-bold text-slate-800',
              cancelled && 'line-through',
            )}
            style={paidColor ? { color: paidColor } : undefined}
          >
            {label}
          </span>
        </span>
        {langKey ? (
          <span
            className={cn(
              'w-full truncate text-center text-[10px] font-semibold uppercase text-slate-500',
              cancelled && 'line-through',
            )}
          >
            {langKey}
            {lesson.numbers > 1 ? ` · ${lesson.numbers}×` : ''}
          </span>
        ) : null}
      </button>

      <span
        className="pointer-events-none absolute left-1 top-1 h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: statusColor ?? '#ccc' }}
        title={statusName}
      />
      {/* Značka inštruktora z mobilného pohľadu (`/i/:token`). */}
      {lesson.attendance === 1 ? (
        <Check size={11} strokeWidth={3} className="pointer-events-none absolute right-1 top-0.5 text-emerald-600" />
      ) : lesson.attendance === 2 ? (
        <UserX size={11} strokeWidth={2.5} className="pointer-events-none absolute right-1 top-0.5 text-rose-600" />
      ) : null}
    </div>
  )
}
